"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { ImageLoaderInterface } from "@/types/image";
import { cn } from "@/lib/utils";

// Generate inline SVG placeholder matching image size
const generatePlaceholder = (
  width: number = 1200,
  height: number = 600,
  text?: string
) => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <rect width="100%" height="100%" fill="#f8fafc"/>
      <text x="50%" y="50%" text-anchor="middle" fill="#64748b" font-size="16" font-family="system-ui, -apple-system, sans-serif" dy=".3em">
        ${text || "Image unavailable"}
      </text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Generate low quality placeholder for progressive loading
const generateLowQualityPlaceholder = (
  width: number = 1200,
  height: number = 600
) => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#f8fafc;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#f1f5f9;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#e2e8f0;stop-opacity:1" />
        </linearGradient>
        <animate attributeName="x1" values="0%;100%;0%" dur="2s" repeatCount="indefinite"/>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)"/>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

export const ImageLoader: React.FC<ImageLoaderInterface> = ({
  src,
  alt,
  width,
  height,
  className,
  skeletonClassName = "w-full h-full",
  fadeInDuration = 300,
  showShimmer = true,
  fallbackSrc = null,
  onLoad = null,
  onError = null,
  fill = false,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  quality = 75,
  priority = false,
  ...props
}) => {
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">("loading");
  const [imageSrc, setImageSrc] = useState(src);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(priority);
  const [showLowQuality, setShowLowQuality] = useState(true);
  const [isMounted, setIsMounted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const fadeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ensure component is mounted before using intersection observer
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Custom image loader with better error handling
  const customImageLoader = useCallback(({ src, width, quality }: { src: string; width: number; quality?: number }) => {
    if (!src || src.startsWith('data:')) return src;
    
    try {
      const url = new URL(src);
      url.searchParams.set('w', width.toString());
      if (quality) {
        url.searchParams.set('q', quality.toString());
      }
      return url.toString();
    } catch {
      return src;
    }
  }, []);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!isMounted || !imgRef.current || priority || isIntersecting) {
      if (priority && !isIntersecting) {
        setIsIntersecting(true);
      }
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }
      },
      {
        rootMargin: "100px", // Start loading earlier
        threshold: 0.01,
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isMounted, priority, isIntersecting]);

  // Reset states when src changes
  useEffect(() => {
    if (imageSrc !== src) {
      setImageState("loading");
      setImageSrc(src);
      setHasTriedFallback(false);
      setShowLowQuality(true);
      setImageLoaded(false);
      
      // Clear any existing fade timer
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    }
  }, [src, imageSrc]);

  const handleImageLoad = useCallback(() => {
    setImageState("loaded");
    setImageLoaded(true);
    
    // Smooth transition from low quality to high quality
    fadeTimerRef.current = setTimeout(() => {
      setShowLowQuality(false);
    }, fadeInDuration);
    
    if (onLoad) onLoad();
  }, [fadeInDuration, onLoad]);

  const handleImageError = useCallback(() => {
    if (fallbackSrc && !hasTriedFallback && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasTriedFallback(true);
      setImageState("loading");
      return;
    }
    
    setImageState("error");
    setShowLowQuality(false);
    if (onError) onError();
  }, [imageSrc, fallbackSrc, hasTriedFallback, onError]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  }, []);

  const defaultFallback = generatePlaceholder(
    fill ? 1200 : width || 1200,
    fill ? 600 : height || 600,
    "Image unavailable"
  );

  const lowQualityPlaceholder = generateLowQualityPlaceholder(
    fill ? 1200 : width || 1200,
    fill ? 600 : height || 600
  );

  // Removed the problematic hover scale animation
  const mergedImageClasses = cn(
    "transition-opacity ease-in-out",
    "object-center object-cover",
    className
  );

  const mergedSkeletonClasses = cn(
    "bg-slate-100",
    "absolute inset-0 flex items-center justify-center",
    skeletonClassName
  );

  const containerClasses = cn(
    "relative overflow-hidden",
    fill ? "w-full h-full" : "",
    !fill && width && height ? `w-[${width}px] h-[${height}px]` : ""
  );

  // Smooth loading spinner
  const LoadingSpinner = () => (
    <div className="relative">
      <div className="w-8 h-8 border-3 border-slate-200 border-t-slate-400 rounded-full animate-spin" />
      <div className="absolute inset-0 w-8 h-8 border-3 border-transparent border-t-slate-600 rounded-full animate-spin opacity-50" 
           style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} />
    </div>
  );

  // Prevent hydration mismatch
  if (!isMounted) {
    return (
      <div ref={imgRef} className={containerClasses}>
        <div
          className={mergedSkeletonClasses}
          style={
            !fill && width && height
              ? { width: `${width}px`, height: `${height}px` }
              : undefined
          }
        >
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div ref={imgRef} className={containerClasses}>
      {/* Loading skeleton with shimmer effect */}
      {imageState === "loading" && (
        <div
          className={cn(
            mergedSkeletonClasses,
            showShimmer && "animate-pulse"
          )}
          style={
            !fill && width && height
              ? { width: `${width}px`, height: `${height}px` }
              : undefined
          }
        >
          <LoadingSpinner />
        </div>
      )}

      {/* Low quality placeholder */}
      {isIntersecting && showLowQuality && imageState !== "error" && (
        <div className="absolute inset-0 z-10">
          <Image
            src={lowQualityPlaceholder}
            alt=""
            width={fill ? undefined : width}
            height={fill ? undefined : height}
            fill={fill}
            className={cn(
              "object-cover transition-opacity duration-500",
              imageLoaded ? "opacity-30" : "opacity-100"
            )}
            priority={priority}
            unoptimized={true}
          />
        </div>
      )}

      {/* High quality image */}
      {isIntersecting && imageState !== "error" && (
        <Image
          src={imageSrc}
          alt={alt}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          fill={fill}
          placeholder="blur"
          blurDataURL={lowQualityPlaceholder}
          sizes={sizes}
          quality={quality}
          priority={priority}
          loader={customImageLoader}
          className={cn(
            mergedImageClasses,
            "absolute inset-0 z-20",
            imageState === "loaded" ? "opacity-100" : "opacity-0"
          )}
          style={{
            transitionDuration: `${fadeInDuration}ms`,
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          {...props}
        />
      )}

      {/* Error fallback */}
      {imageState === "error" && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center bg-slate-50 border-2 border-dashed border-slate-200"
          style={
            !fill && width && height
              ? { width: `${width}px`, height: `${height}px` }
              : undefined
          }
        >
          <Image
            src={defaultFallback}
            alt={`${alt} (unavailable)`}
            width={fill ? undefined : width}
            height={fill ? undefined : height}
            fill={fill}
            className="object-cover opacity-75"
            priority={priority}
            unoptimized={true}
          />
        </div>
      )}
    </div>
  );
};